import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { UseAppToast } from "src/common/useAppToast";
import { ConnectionsService } from "src/rest/connectionsService";
import type { ConnectionRequestDto } from "src/rest/connectionsService";
import { InterestProfileCard } from "../components/interestProfileCard";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { useSelector } from "react-redux";
import type { RootState } from "src/store/store";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { HindiLabels } from "src/common/constants/hindiLabels";

type Props = {
  marriageProfileId: string | number;
  items: ConnectionRequestDto[];
  isLoading?: boolean;
  onRefresh: () => void;
  onAcceptedNavigateToConnections: () => void;
};

const ReceivedContractRequest: React.FC<Props> = ({ items, onRefresh, onAcceptedNavigateToConnections }) => {
  const { showErrorToast, showSuccessToast } = UseAppToast();

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const isHindiLabels = globalInfo.globalLanguage === "HI";
  const labels = isHindiLabels ? HindiLabels : EnglishLabels;

  const [modalRequestId, setModalRequestId] = React.useState<string | number | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [avatarUrlsByRequestId, setAvatarUrlsByRequestId] = React.useState<Record<string, string>>({});

  const computeAge = (isoDob?: string | null): number => {
    if (!isoDob) return 0;
    const d = new Date(isoDob);
    if (Number.isNaN(d.getTime())) return 0;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return Math.max(0, age);
  };

  React.useEffect(() => {
    return () => {
      Object.values(avatarUrlsByRequestId).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          // ignore
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        (items as any[]).map(async (req) => {
          const requestId = String((req as any)?.requestId ?? (req as any)?.id ?? "");
          const image = String((req as any)?.profileImage || "");
          if (!requestId || !image) return null;

          try {
            const url = await fetchAuthedImageObjectUrl(image);
            return [requestId, url] as const;
          } catch {
            return null;
          }
        })
      );

      if (cancelled) {
        entries.forEach((e) => {
          if (e?.[1]) URL.revokeObjectURL(e[1]);
        });
        return;
      }

      setAvatarUrlsByRequestId((prev) => {
        const next = { ...prev };
        entries.forEach((e) => {
          if (!e) return;
          const [id, url] = e;
          if (next[id] && next[id] !== url) {
            try {
              URL.revokeObjectURL(next[id]);
            } catch {
              // ignore
            }
          }
          next[id] = url;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [items]);

  const closeModal = () => setModalRequestId(null);

  const handleAccept = async (requestId: string | number) => {
    try {
      setIsUpdating(true);
      const res = await ConnectionsService.updateConnectionStatus(requestId, "ACCEPTED");
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Request failed (HTTP ${status})`);
      }
      showSuccessToast(String(labels.Connection_Successful));
      onAcceptedNavigateToConnections?.();
      onRefresh();
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || e?.message || String(labels.Unable_to_accept_request));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDecline = (requestId: string | number) => {
    setModalRequestId(requestId);
  };

  const confirmDecline = async () => {
    if (modalRequestId == null) return;
    try {
      setIsUpdating(true);
      const res = await ConnectionsService.updateConnectionStatus(modalRequestId, "DECLINED");
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Request failed (HTTP ${status})`);
      }
      closeModal();
      window.location.reload();
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || e?.message || String(labels.Unable_to_decline_request));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <VStack spacing={5} align="stretch">
        {!items.length ? (
          <Text color="#4A2C2A">{String(labels.No_received_requests)}</Text>
        ) : (
          (items as ConnectionRequestDto[]).map((req) => {
            const requestId = (req as any)?.requestId ?? (req as any)?.id;
            return (
              <InterestProfileCard
                key={String(requestId)}
                variant="received"
                name={String((req as any)?.fullName || "")}
                age={computeAge((req as any)?.dateOfBirth)}
                height={String((req as any)?.heightInCm ? `${(req as any).heightInCm} cm` : "")}
                subtitle={String((req as any)?.designation || "")}
                timeLabel={""}
                avatarUrl={avatarUrlsByRequestId[String(requestId)]}
                status={String((req as any)?.status || "")}

                // details
                dateOfBirth={String((req as any)?.dateOfBirth || "")}
                heightInCm={(req as any)?.heightInCm ?? undefined}
                designation={String((req as any)?.designation || "")}
                employmentSector={String((req as any)?.employmentSector || "")}
                employerName={String((req as any)?.employerName || "")}

                // contact (may be present for accepted lists)
                email={String((req as any)?.email || "")}
                phone={String((req as any)?.mobile || (req as any)?.mobileNo || "")}

                onAccept={() => handleAccept(requestId)}
                onDecline={() => handleDecline(requestId)}
              />
            );
          })
        )}
      </VStack>

      <Modal isOpen={modalRequestId != null} onClose={closeModal} isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent borderRadius="18px" p={{ base: 4, md: 6 }}>
          <ModalCloseButton />
          <ModalHeader textAlign="center" fontSize="xl" fontWeight="800" color="#1F0812">
            {String(labels.Decline_Request_Confirmation)}
          </ModalHeader>
          <ModalBody>
            <Text textAlign="center" color="#6C757D" fontSize="sm" lineHeight="1.6">
              {String(labels.Decline_Request_Confirmation_Desc)}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Flex w="full" direction="column" gap={3}>
              <Button
                w="full"
                borderRadius="999px"
                variant="outline"
                borderColor="#4A2C2A"
                color="#4A2C2A"
                onClick={confirmDecline}
                isLoading={isUpdating}
              >
                {String(labels.Yes_Decline)}
              </Button>
              <Button w="full" variant="ghost" color="#6C757D" onClick={closeModal}>
                {String(labels.Keep_for_now)}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReceivedContractRequest;
